import { useSyncExternalStore } from "react";
import { applyBrandColors } from "../configs/colors";
import {
  StoreDateFormat,
  StoreTimeFormat,
} from "../typings/interfaces/responses/store-setup-response";

// The store's look from Admin → Store Setup: brand colours, logo and the
// date / time format. Null until a store's setup has been saved (Presto
// defaults). Kept in localStorage so the app opens in it straight away.

export interface IStoreBranding {
  primaryColor?: string | null;
  secondaryColor?: string | null;
  /** data:image/... URL */
  logo?: string | null;
  dateFormat?: StoreDateFormat | null;
  timeFormat?: StoreTimeFormat | null;
}

const KEY = "storeBranding";

const read = (): IStoreBranding | null => {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "null");
  } catch {
    return null;
  }
};

let branding: IStoreBranding | null = read();
// Bumped when the colours change: the app re-mounts its pages so styles
// worked out from Colors at render pick up the new values
let colorVersion = 0;
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getStoreBranding = () => branding;

export const setStoreBranding = (next: IStoreBranding | null) => {
  const colorsChanged = applyBrandColors(next?.primaryColor, next?.secondaryColor);
  const changed = colorsChanged || JSON.stringify(next) !== JSON.stringify(branding);
  if (!changed) return;
  branding = next;
  if (colorsChanged) colorVersion += 1;
  try {
    if (next) localStorage.setItem(KEY, JSON.stringify(next));
    else localStorage.removeItem(KEY);
  } catch {
    // e.g. a big logo over the storage quota: still applies for this session
  }
  listeners.forEach((l) => l());
};

export const useStoreBranding = () => useSyncExternalStore(subscribe, () => branding);

export const useBrandColorVersion = () => useSyncExternalStore(subscribe, () => colorVersion);
